// useRememberedOpenBucket.js

import { useEffect, useMemo, useState } from 'react';

// Constants
const SIDEBAR_BUCKET_STORAGE_KEY = 'nodal:lastOpenSidebarBucketByScope:v1';
const CLOSED_BUCKET_SENTINEL = '__closed__';

// Utilities
const normalize = (value) => String(value || '').trim();
const normalizeScopePart = (value) => normalize(value).toLowerCase();

const getBucketScopeMemoryKey = ({ workspaceKey, userKey }) => {
    const workspace = normalizeScopePart(workspaceKey);
    const user = normalizeScopePart(userKey);
    if (!workspace || !user) return '';
    return `${user}::${workspace}`;
};

const getDefaultOpenBucketKey = ({ buckets }) => {
    const bucketList = Array.isArray(buckets) ? buckets : [];
    if (bucketList.length !== 1) return null;

    const onlyBucketKey = normalize(bucketList[0]?.key);
    return onlyBucketKey === 'workspace' ? onlyBucketKey : null;
};

const readStoredBucketMap = () => {
    if (typeof window === 'undefined' || !window?.localStorage) return {};

    try {
        const raw = window.localStorage.getItem(SIDEBAR_BUCKET_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const writeStoredBucketMap = (nextMap) => {
    if (typeof window === 'undefined' || !window?.localStorage) return;

    try {
        window.localStorage.setItem(SIDEBAR_BUCKET_STORAGE_KEY, JSON.stringify(nextMap || {}));
    } catch {
        // Ignore storage failures; sidebar should still work in-memory.
    }
};

const getStoredBucketPreferenceForScope = (scopeMemoryKey) => {
    if (!scopeMemoryKey) {
        return {
            hasStoredPreference: false,
            openBucketKey: null,
        };
    }

    const map = readStoredBucketMap();
    if (!Object.prototype.hasOwnProperty.call(map, scopeMemoryKey)) {
        return {
            hasStoredPreference: false,
            openBucketKey: null,
        };
    }

    const stored = normalize(map?.[scopeMemoryKey]);
    if (!stored) {
        return {
            hasStoredPreference: false,
            openBucketKey: null,
        };
    }

    return {
        hasStoredPreference: true,
        openBucketKey: stored === CLOSED_BUCKET_SENTINEL ? null : stored,
    };
};

const setStoredBucketPreferenceForScope = (scopeMemoryKey, bucketKey) => {
    if (!scopeMemoryKey) return;

    const normalizedKey = normalize(bucketKey) || CLOSED_BUCKET_SENTINEL;

    const map = readStoredBucketMap();
    if (map?.[scopeMemoryKey] === normalizedKey) return;

    writeStoredBucketMap({
        ...map,
        [scopeMemoryKey]: normalizedKey,
    });
};

/**
 * Persists and restores open sidebar bucket state for a user/workspace scope.
 *
 * @param {string} userKey - Active user key.
 * @param {string} workspaceKey - Active workspace key.
 * @param {array} buckets - Current bucket list.
 * @param {boolean} modulesHydrated - Whether module buckets have hydrated.
 *
 * @returns {object} Open bucket state and setter.
 */
const useRememberedOpenBucket = ({
    userKey,
    workspaceKey,
    buckets,
    modulesHydrated,
}) => {
    // State
    const [openBucketKey, setOpenBucketKey] = useState(null);
    const [bucketMemoryHydrated, setBucketMemoryHydrated] = useState(false);
    const [bucketPreferenceReady, setBucketPreferenceReady] = useState(false);

    // Memo
    const bucketScopeMemoryKey = useMemo(() => getBucketScopeMemoryKey({
        workspaceKey,
        userKey,
    }), [workspaceKey, userKey]);

    // Effects

    // Hydrate remembered bucket for the active scope.
    useEffect(() => {
        if (!bucketScopeMemoryKey) {
            setOpenBucketKey(null);
            setBucketMemoryHydrated(false);
            setBucketPreferenceReady(false);
            return;
        }

        const storedPreference = getStoredBucketPreferenceForScope(bucketScopeMemoryKey);
        setOpenBucketKey(storedPreference.openBucketKey);
        setBucketMemoryHydrated(true);
        setBucketPreferenceReady(storedPreference.hasStoredPreference);
    }, [bucketScopeMemoryKey]);

    // Default first-run state to the workspace bucket when it is the only bucket.
    useEffect(() => {
        if (!bucketMemoryHydrated || bucketPreferenceReady) return;

        if (openBucketKey) {
            setBucketPreferenceReady(true);
            return;
        }

        if (!modulesHydrated) return;

        const defaultBucketKey = getDefaultOpenBucketKey({ buckets });
        if (defaultBucketKey) {
            setOpenBucketKey(defaultBucketKey);
            return;
        }

        setBucketPreferenceReady(true);
    }, [bucketMemoryHydrated, bucketPreferenceReady, buckets, modulesHydrated, openBucketKey]);

    // Reset remembered key if it no longer exists in available buckets.
    useEffect(() => {
        if (!openBucketKey) return;
        const exists = (Array.isArray(buckets) ? buckets : []).some((bucket) => bucket.key === openBucketKey);
        if (!exists) {
            if (openBucketKey.startsWith('module:') && !modulesHydrated) return;
            setOpenBucketKey(null);
        }
    }, [buckets, modulesHydrated, openBucketKey]);

    // Persist current open bucket for the active scope.
    useEffect(() => {
        if (!bucketMemoryHydrated || !bucketPreferenceReady || !bucketScopeMemoryKey) return;

        setStoredBucketPreferenceForScope(bucketScopeMemoryKey, openBucketKey);
    }, [bucketMemoryHydrated, bucketPreferenceReady, bucketScopeMemoryKey, openBucketKey]);

    return {
        openBucketKey,
        setOpenBucketKey,
    };
};

export default useRememberedOpenBucket;